// ScopeWorth Product page — sections

// ============= HERO =============
import React from 'react';
import { PageHero } from './Shell.jsx';
import { localizedHref } from './i18n/bootstrap.js';
import { t } from './i18n/useT.js';

export function ProductHero() {
  return (
    <div id="top">
      <PageHero
        showcase
        overlapNav
        eyebrow={t('product.hero.eyebrow')}
        title={<>{t('product.hero.title')}{' '}<span className="sw-hero-title-accent">{t('product.hero.titleAccent')}</span></>}
        lead={t('product.hero.sub')}
        visual={<ProductHeroReport/>}
      >
        <button className="sw-btn sw-btn-primary sw-btn-lg" onClick={() => window.openDemoTour?.('product-hero')}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5.5v13a1 1 0 0 0 1.55.83l10-6.5a1 1 0 0 0 0-1.66l-10-6.5A1 1 0 0 0 8 5.5Z"/></svg>
          <span className="sw-btn-label">{t('product.video.watch')}</span>
        </button>
        <a className="sw-btn sw-btn-ghost-dark sw-btn-lg" href={localizedHref('/integrations')}>
          <span className="sw-btn-label">{t('product.hero.integrations')}</span>
        </a>
      </PageHero>
    </div>
  );
}

// The hero player is a mock around the real thing: the play control opens the
// same demo tour the home page uses, and the stage shows a muted 5.7s cut of
// that walkthrough (2:03 in, where the command centre counts its KPIs up)
// instead of a drawn poster — motion in the frame without asking for a click.
//
// The transport numbers are derived from the walkthrough itself rather than
// typed in, so the mock cannot drift from the video it launches.
const DEMO_RUNTIME_S = 525;      // 8:45 — matches demoModal.meta
const DEMO_PREVIEW_AT_S = 123;   // where the looping cut sits in the walkthrough
const DEMO_PREVIEW_LOOP = '/marketing/videos/demo-walkthrough/demo-preview-loop.mp4';
const DEMO_PREVIEW_POSTER = '/marketing/videos/demo-walkthrough/demo-preview-poster.jpg';
const DEMO_PREVIEW_POSTER_960 = '/marketing/videos/demo-walkthrough/demo-preview-poster-960.jpg';

function clock(seconds, pad = false) {
  const m = Math.floor(seconds / 60);
  const s = String(Math.round(seconds % 60)).padStart(2, '0');
  return `${pad ? String(m).padStart(2, '0') : m}:${s}`;
}

function ProductHeroReport() {
  const videoRef = React.useRef(null);
  const [still, setStill] = React.useState(() =>
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  );

  React.useEffect(() => {
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setStill(motion.matches);
    motion.addEventListener?.('change', onChange);
    return () => motion.removeEventListener?.('change', onChange);
  }, []);

  // Playback waits for the window `load` event so the loop never competes with
  // the hero's LCP; `preload="none"` means nothing is fetched until then.
  React.useEffect(() => {
    if (still) return undefined;
    const start = () => {
      const v = videoRef.current;
      if (!v) return;
      v.load();
      v.play().catch(() => { /* autoplay refused — the poster stays */ });
    };
    if (document.readyState === 'complete') { start(); return undefined; }
    window.addEventListener('load', start, { once: true });
    return () => window.removeEventListener('load', start);
  }, [still]);

  const openTour = () => window.openDemoTour?.('product-hero');
  const progress = `${(DEMO_PREVIEW_AT_S / DEMO_RUNTIME_S) * 100}%`;

  return (
    <div className="sw-video" role="region" aria-label={t('product.video.regionLabel')}>
      <div className="sw-video-frame">
        <div className="sw-video-chrome">
          <div className="sw-video-traffic"><i/><i/><i/></div>
          <div className="sw-video-title">{t('product.video.title')}</div>
          <div className="sw-video-stamp">{clock(DEMO_RUNTIME_S)}</div>
        </div>
        <div className="sw-video-stage">
          {/* Decorative: the play button below is the accessible control, and
              clicking the frame anywhere opens the same tour. */}
          <div className="sw-video-preview" aria-hidden="true" onClick={openTour}>
            {still ? (
              <img
                className="sw-video-preview-media"
                src={DEMO_PREVIEW_POSTER}
                srcSet={`${DEMO_PREVIEW_POSTER_960} 960w, ${DEMO_PREVIEW_POSTER} 1920w`}
                sizes="min(92vw, 1120px)"
                width="1920"
                height="1080"
                alt=""
                decoding="async"
              />
            ) : (
              <video
                ref={videoRef}
                className="sw-video-preview-media"
                muted
                loop
                playsInline
                preload="none"
                poster={DEMO_PREVIEW_POSTER}
                src={DEMO_PREVIEW_LOOP}
                tabIndex={-1}
              />
            )}
            <div className="sw-video-preview-veil"></div>
          </div>

          <button
            className="sw-video-play"
            aria-label={t('product.video.play')}
            onClick={openTour}
          >
            <span className="sw-video-play-ring"/>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5.5v13a1 1 0 0 0 1.55.83l10-6.5a1 1 0 0 0 0-1.66l-10-6.5A1 1 0 0 0 8 5.5Z"/></svg>
          </button>

          <div className="sw-video-chips">
            <span className="sw-video-chip"><span className="sw-eyebrow-dot"/>{t('product.video.live')}</span>
          </div>
        </div>
        {/* A transport, not a player: the play control opens the tour and the
            rest reads the walkthrough's own timings. */}
        <div className="sw-video-controls">
          <button className="sw-video-ctrl" aria-label={t('product.video.play')} onClick={openTour}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
          </button>
          <div className="sw-video-time">{clock(DEMO_PREVIEW_AT_S, true)}</div>
          <div className="sw-video-scrub" aria-hidden="true">
            <span className="sw-video-scrub-fill" style={{ width: progress }}/>
            <span className="sw-video-scrub-knob" style={{ left: progress }}/>
          </div>
          <div className="sw-video-time">{clock(DEMO_RUNTIME_S, true)}</div>
          <span className="sw-video-ctrl" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 5 6 9H2v6h4l5 4zM15.5 8.5a5 5 0 0 1 0 7M19 5a9 9 0 0 1 0 14"/></svg>
          </span>
          <span className="sw-video-ctrl" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"/></svg>
          </span>
        </div>
      </div>
      <p className="sw-demo-modal-meta">{t('demoModal.disclosure')}</p>
    </div>
  );
}

// ============= 2. PRODUCT PROMISE =============
export function ProductPromiseSection() {
  const icons = ['shield', 'coin', 'lock'];
  const cards = t('product.promise.cards');
  return (
    <section className="sw-section sw-section-light" id="promise">
      <div className="sw-container">
        <div className="sw-section-head sw-section-head-center">
          <div className="sw-eyebrow">{t('product.promise.eyebrow')}</div>
          <h2 className="sw-h2">{t('product.promise.title')}</h2>
          <p className="sw-lead">{t('product.promise.lead')}</p>
        </div>
        <div className="sw-promise-grid">
          {cards.map((c, i) => (
            <div key={i} className="sw-promise-card">
              <div className="sw-promise-icon"><PromiseIcon kind={icons[i]}/></div>
              <h3 className="sw-promise-title">{c.title}</h3>
              <p className="sw-promise-body">{c.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PromiseIcon({ kind }) {
  const s = { strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round', fill: 'none', stroke: 'currentColor' };
  if (kind === 'shield') return <svg width="26" height="26" viewBox="0 0 24 24" {...s}><path d="M12 3 4 6v6c0 5 3.5 8.5 8 9 4.5-.5 8-4 8-9V6l-8-3Z"/><path d="m9 12 2 2 4-4"/></svg>;
  if (kind === 'coin') return <svg width="26" height="26" viewBox="0 0 24 24" {...s}><circle cx="12" cy="12" r="9"/><path d="M14 8.5h-3.5a1.5 1.5 0 0 0 0 3h3a1.5 1.5 0 0 1 0 3H10M12 7v1.5M12 14.5V16"/></svg>;
  if (kind === 'lock') return <svg width="26" height="26" viewBox="0 0 24 24" {...s}><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>;
  return null;
}

// ============= SECTION-HEAD GLYPHS =============
function SectionHeadGlyph({ kind }) {
  const s = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.7, strokeLinecap: 'round', strokeLinejoin: 'round' };
  if (kind === 'problem-fix') return (
    <svg width="52" height="52" viewBox="0 0 54 54" aria-hidden="true" {...s}>
      {/* Fork: single input splits into problem (left) and fix (right) */}
      <path d="M27 4 L27 16"/>
      <path d="M27 16 C27 22 14 24 14 36"/>
      <path d="M27 16 C27 22 40 24 40 36"/>
      {/* Problem = X */}
      <circle cx="14" cy="36" r="9"/>
      <path d="M9 31 L19 41 M19 31 L9 41"/>
      {/* Fix = check */}
      <circle cx="40" cy="36" r="9"/>
      <path d="M33 36 L38 41 L47 31"/>
    </svg>
  );
  if (kind === 'modules') return (
    <svg width="52" height="52" viewBox="0 0 54 54" aria-hidden="true" {...s}>
      {/* 3×2 grid of module tiles */}
      <rect x="2" y="2" width="14" height="22" rx="3"/>
      <rect x="20" y="2" width="14" height="22" rx="3"/>
      <rect x="38" y="2" width="14" height="22" rx="3"/>
      <rect x="2" y="30" width="14" height="22" rx="3"/>
      <rect x="20" y="30" width="14" height="22" rx="3"/>
      <rect x="38" y="30" width="14" height="22" rx="3"/>
      <path d="M5 10 L13 10 M5 14 L11 14"/>
      <path d="M23 10 L31 10 M23 14 L29 14"/>
      <path d="M41 10 L49 10 M41 14 L47 14"/>
      <path d="M5 38 L13 38 M5 42 L11 42"/>
      <path d="M23 38 L31 38 M23 42 L29 42"/>
      <path d="M41 38 L49 38 M41 42 L47 42"/>
    </svg>
  );
  if (kind === 'pipeline') return (
    <svg width="52" height="52" viewBox="0 0 54 54" aria-hidden="true" {...s}>
      {/* 3-step workflow */}
      <rect x="2" y="16" width="13" height="22" rx="3"/>
      <rect x="20" y="16" width="13" height="22" rx="3"/>
      <rect x="39" y="16" width="13" height="22" rx="3"/>
      <path d="M15 27 L20 27"/>
      <path d="M17.5 24.5 L20 27 L17.5 29.5"/>
      <path d="M33 27 L39 27"/>
      <path d="M36 24.5 L39 27 L36 29.5"/>
      <path d="M5 22 L12 22 M5 27 L10 27 M5 32 L8 32"/>
      <path d="M23 22 L30 22 M23 27 L28 27 M23 32 L26 32"/>
      <path d="M42 22 L49 22 M42 27 L47 27 M42 32 L45 32"/>
    </svg>
  );
  if (kind === 'report-doc') return (
    <svg width="52" height="52" viewBox="0 0 54 54" aria-hidden="true" {...s}>
      {/* Document with bar chart */}
      <path d="M10 4 L36 4 L44 12 L44 50 L10 50 Z"/>
      <path d="M36 4 L36 12 L44 12"/>
      <path d="M15 20 L38 20 M15 25 L32 25"/>
      <path d="M16 44 L16 36 M22 44 L22 30 M28 44 L28 38 M34 44 L34 28 M40 44 L40 34"/>
      <path d="M14 44 L42 44"/>
    </svg>
  );
  if (kind === 'agency') return (
    <svg width="52" height="52" viewBox="0 0 54 54" aria-hidden="true" {...s}>
      {/* Presentation screen with bar chart */}
      <rect x="2" y="6" width="26" height="20" rx="3"/>
      <path d="M15 26 L15 32 M10 32 L20 32"/>
      <path d="M6 22 L6 14 M11 22 L11 10 M16 22 L16 17 M22 22 L22 12"/>
      <path d="M5 22 L24 22"/>
      {/* Handoff arrow */}
      <path d="M30 16 L44 16"/>
      <path d="M40 12 L44 16 L40 20"/>
      {/* Received document */}
      <path d="M44 26 L50 26 L52 28 L52 50 L44 50 Z"/>
      <path d="M50 26 L50 28 L52 28"/>
      <path d="M46 34 L50 34 M46 38 L50 38 M46 43 L50 43"/>
    </svg>
  );
  if (kind === 'integrations') return (
    <svg width="52" height="52" viewBox="0 0 54 54" aria-hidden="true" {...s}>
      {/* Hub-and-spoke: central hub + 4 tool nodes */}
      <line x1="27" y1="27" x2="27" y2="8"/>
      <line x1="27" y1="27" x2="46" y2="27"/>
      <line x1="27" y1="27" x2="27" y2="46"/>
      <line x1="27" y1="27" x2="8" y2="27"/>
      <circle cx="27" cy="27" r="8"/>
      <rect x="21" y="2" width="12" height="12" rx="3"/>
      <rect x="40" y="21" width="12" height="12" rx="3"/>
      <rect x="21" y="40" width="12" height="12" rx="3"/>
      <rect x="2" y="21" width="12" height="12" rx="3"/>
    </svg>
  );
  if (kind === 'principles') return (
    <svg width="52" height="52" viewBox="0 0 54 54" aria-hidden="true" {...s}>
      {/* Shield with checklist */}
      <path d="M27 4 L6 12 L6 26 C6 38 15 46 27 50 C39 46 48 38 48 26 L48 12 Z"/>
      <path d="M16 22 L19 25 L24 19"/>
      <path d="M16 31 L19 34 L24 28"/>
      <path d="M28 22 L38 22"/>
      <path d="M28 31 L38 31"/>
    </svg>
  );
  return null;
}

// ============= 3. PROBLEM AND FIX =============
export function ProblemFixSection() {
  const cards = t('product.problemFix.cards');
  return (
    <section className="sw-section sw-section-light sw-section-pale" id="problem-fix">
      <div className="sw-container">
        <div className="sw-section-head sw-section-head-2col">
          <div className="sw-sh-text">
            <div className="sw-eyebrow">{t('product.problemFix.eyebrow')}</div>
            <h2 className="sw-h2">{t('product.problemFix.title')}</h2>
            <p className="sw-lead">{t('product.problemFix.lead')}</p>
          </div>
          <div className="sw-sh-icon sw-sh-icon-light" aria-hidden="true">
            <SectionHeadGlyph kind="problem-fix"/>
          </div>
        </div>
        <div className="sw-problem-grid">
          {cards.map((c, i) => (
            <article key={i} className="sw-problem-card">
              <div className="sw-problem-num">0{i + 1}</div>
              <h3 className="sw-problem-title">{c.title}</h3>
              <div className="sw-problem-block">
                <div className="sw-problem-tag sw-problem-tag-problem">{t('product.problemFix.tagProblem')}</div>
                <p className="sw-problem-body">{c.problem}</p>
              </div>
              <div className="sw-problem-block">
                <div className="sw-problem-tag sw-problem-tag-fix">{t('product.problemFix.tagFix')}</div>
                <p className="sw-problem-body">{c.fix}</p>
              </div>
              <div className="sw-problem-example">
                <div className="sw-problem-example-label">{t('product.problemFix.tagProof')}</div>
                <div className="sw-problem-example-body">{c.example}</div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============= 4. CORE PRODUCT MODULES =============
export function ProductModulesSection() {
  const icons = ['doc', 'compass', 'coin', 'sparkle', 'graph', 'present'];
  const modules = t('product.modules.items');
  return (
    <section className="sw-section sw-section-dark" id="modules">
      <div className="sw-container">
        <div className="sw-section-head sw-section-head-2col">
          <div className="sw-sh-text">
            <div className="sw-eyebrow sw-eyebrow-teal">{t('product.modules.eyebrow')}</div>
            <h2 className="sw-h2 sw-h2-dark">{t('product.modules.title')}</h2>
            <p className="sw-lead sw-lead-dark">{t('product.modules.lead')}</p>
          </div>
          <div className="sw-sh-icon sw-sh-icon-dark" aria-hidden="true">
            <SectionHeadGlyph kind="modules"/>
          </div>
        </div>
        <div className="sw-mod-grid">
          {modules.map((m, i) => (
            <article key={i} className="sw-mod-card">
              <div className="sw-mod-head">
                <div className="sw-mod-icon"><ModBigIcon kind={icons[i]}/></div>
                <div className="sw-mod-eyebrow">{t('product.modules.moduleLabel')} 0{i + 1}</div>
              </div>
              <h3 className="sw-mod-title">{m.title}</h3>
              <p className="sw-mod-desc">{m.desc}</p>
              <div className="sw-mod-caps-label">{t('product.modules.capsLabel')}</div>
              <ul className="sw-mod-caps">
                {m.caps.map((c, j) => (
                  <li key={j}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 5 5L20 7"/></svg>
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
              <div className="sw-mod-outcome">
                <div className="sw-mod-outcome-label">{t('product.modules.outcomeLabel')}</div>
                <div className="sw-mod-outcome-body">{m.outcome}</div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function ModBigIcon({ kind }) {
  const s = { strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round', fill: 'none', stroke: 'currentColor' };
  const map = {
    doc: <svg width="22" height="22" viewBox="0 0 24 24" {...s}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M9 13h6M9 17h4"/></svg>,
    compass: <svg width="22" height="22" viewBox="0 0 24 24" {...s}><circle cx="12" cy="12" r="9"/><path d="m16 8-2 6-6 2 2-6 6-2Z"/></svg>,
    coin: <svg width="22" height="22" viewBox="0 0 24 24" {...s}><circle cx="12" cy="12" r="9"/><path d="M14 8.5h-3.5a1.5 1.5 0 0 0 0 3h3a1.5 1.5 0 0 1 0 3H10M12 7v1.5M12 14.5V16"/></svg>,
    sparkle: <svg width="22" height="22" viewBox="0 0 24 24" {...s}><path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1"/><circle cx="12" cy="12" r="3"/></svg>,
    graph: <svg width="22" height="22" viewBox="0 0 24 24" {...s}><circle cx="6" cy="6" r="2.5"/><circle cx="18" cy="6" r="2.5"/><circle cx="12" cy="18" r="2.5"/><path d="M7.7 7.7 10.3 16M16.3 7.7 13.7 16M8 6h8"/></svg>,
    present: <svg width="22" height="22" viewBox="0 0 24 24" {...s}><path d="M3 4h18v12H3zM12 16v5M8 21h8M7 10l3 3 3-4 4 4"/></svg>,
    shield: <svg width="22" height="22" viewBox="0 0 24 24" {...s}><path d="M12 3 4 6v6c0 5 3.5 8.5 8 9 4.5-.5 8-4 8-9V6l-8-3Z"/><path d="M9 10h6M9 13h6M9 16h4"/></svg>,
  };
  return map[kind] || null;
}

// ============= 5. HOW IT WORKS =============
export function HowProductWorksSection() {
  const steps = t('product.how.steps');
  return (
    <section className="sw-section sw-section-light" id="how">
      <div className="sw-container">
        <div className="sw-section-head sw-section-head-2col">
          <div className="sw-sh-text">
            <div className="sw-eyebrow">{t('product.how.eyebrow')}</div>
            <h2 className="sw-h2">{t('product.how.title')}</h2>
            <p className="sw-lead">{t('product.how.lead')}</p>
          </div>
          <div className="sw-sh-icon sw-sh-icon-light" aria-hidden="true">
            <SectionHeadGlyph kind="pipeline"/>
          </div>
        </div>
        <div className="sw-flow">
          {steps.map((s, i) => (
            <div key={i} className="sw-flow-step">
              <div className="sw-flow-step-num">0{i + 1}</div>
              <div className="sw-flow-step-rail" aria-hidden="true">
                <span className="sw-flow-step-dot"/>
                {i < steps.length - 1 && <span className="sw-flow-step-line"/>}
              </div>
              <div className="sw-flow-step-body">
                <h3 className="sw-flow-step-title">{s.t}</h3>
                <p className="sw-flow-step-desc">{s.b}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============= 7. AGENCY USE CASES =============
export function AgencyUseCasesSection() {
  const icons = ['calendar', 'shield', 'compass', 'pie', 'sparkle', 'coin'];
  const cases = t('product.agency.cases');
  return (
    <section className="sw-section sw-section-light" id="agency-cases">
      <div className="sw-container">
        <div className="sw-section-head sw-section-head-2col">
          <div className="sw-sh-text">
            <div className="sw-eyebrow">{t('product.agency.eyebrow')}</div>
            <h2 className="sw-h2">{t('product.agency.title')}</h2>
            <p className="sw-lead">{t('product.agency.lead')}</p>
          </div>
          <div className="sw-sh-icon sw-sh-icon-light" aria-hidden="true">
            <SectionHeadGlyph kind="agency"/>
          </div>
        </div>
        <div className="sw-uc-grid">
          {cases.map((c, i) => (
            <div key={i} className="sw-uc-card">
              <div className="sw-uc-icon"><ModBigIcon kind={icons[i] === 'calendar' ? 'doc' : icons[i] === 'pie' ? 'graph' : icons[i]}/></div>
              <h3 className="sw-uc-title">{c.t}</h3>
              <p className="sw-uc-body">{c.b}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============= 9. ANTI-SURVEILLANCE =============
// Side-by-side data-boundaries comparison: what ScopeWorth collects
// (metadata-first signals from the prototype's data-sources view) vs what
// it explicitly does NOT collect (anti-surveillance boundary cards).
export function AntiSurveillanceSection() {
  const cards = t('product.antiSurveillance.cards');
  const collected = t('product.antiSurveillance.collected');
  const notCollected = t('product.antiSurveillance.notCollected');
  return (
    <section className="sw-section sw-section-light sw-section-pale" id="anti-surveillance">
      <div className="sw-container">
        <div className="sw-section-head sw-section-head-center">
          <div className="sw-eyebrow sw-eyebrow-teal">{t('product.antiSurveillance.eyebrow')}</div>
          <h2 className="sw-h2">{t('product.antiSurveillance.title')}</h2>
          <p className="sw-lead">{t('product.antiSurveillance.lead')}</p>
        </div>
        <div className="sw-trust-grid-3">
          {cards.map((c, i) => (
            <div key={i} className="sw-trust-card">
              <div className="sw-trust-card-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3 4 6v6c0 5 3.5 8.5 8 9 4.5-.5 8-4 8-9V6l-8-3Z"/><path d="m9 12 2 2 4-4"/></svg>
              </div>
              <h3 className="sw-trust-card-t">{c.t}</h3>
              <p className="sw-trust-card-b">{c.b}</p>
            </div>
          ))}
        </div>
        <div className="sw-boundary-compare">
          <div className="sw-boundary-card is-collect">
            <div className="sw-boundary-card-h">
              <span className="sw-eyebrow sw-eyebrow-teal">{t('product.antiSurveillance.collectLabel')}</span>
              <span className="sw-boundary-tag is-teal">{t('product.antiSurveillance.collectTag')}</span>
            </div>
            <ul className="sw-boundary-list">
              {collected.map(item => (
                <li key={item}>
                  <span className="sw-boundary-mark is-teal" aria-hidden="true">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 5 5L20 7"/></svg>
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="sw-boundary-card is-never">
            <div className="sw-boundary-card-h">
              <span className="sw-eyebrow" style={{ color: 'var(--danger)' }}>{t('product.antiSurveillance.neverLabel')}</span>
              <span className="sw-boundary-tag is-red">{t('product.antiSurveillance.neverTag')}</span>
            </div>
            <ul className="sw-boundary-list">
              {notCollected.map(item => (
                <li key={item}>
                  <span className="sw-boundary-mark is-red" aria-hidden="true">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 6l12 12M18 6L6 18"/></svg>
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============= 13. FAQ =============
export function ProductFaqSection() {
  const faqs = t('product.faq.items');
  const [open, setOpen] = React.useState(0);
  return (
    <section className="sw-section sw-section-light" id="faq">
      <div className="sw-container">
        <div className="sw-faq-grid">
          <div className="sw-faq-aside">
            <div className="sw-eyebrow">{t('product.faq.eyebrow')}</div>
            <h2 className="sw-h2">{t('product.faq.title')}</h2>
            <p className="sw-lead">{t('product.faq.leadPrefix')}<a href={localizedHref('/contact')} className="sw-textlink">{t('product.faq.leadLink')}</a>{t('product.faq.leadSuffix')}</p>
          </div>
          <div className="sw-faq-list">
            {faqs.map((f, i) => (
              <div key={i} className={`sw-faq-item ${open === i ? 'is-open' : ''}`}>
                <button className="sw-faq-q" onClick={() => setOpen(open === i ? -1 : i)} aria-expanded={open === i} aria-controls={`sw-product-faq-a-${i}`}>
                  <span>{f.q}</span>
                  <span className="sw-faq-icon" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 9l6 6 6-6"/></svg>
                  </span>
                </button>
                <div className="sw-faq-a" id={`sw-product-faq-a-${i}`} hidden={open !== i}>{f.a}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ============= ANCHOR INDEX (product page) =============
// On-page anchor index, styled like the homepage trust strip and shown right after
// the product hero. Anchored sections use scroll-margin-top so the sticky nav
// doesn't cover the section title when smooth-scrolling.
// Ordered to match the actual on-page section flow, and each href targets a
// real rendered section id (see product.jsx / DashboardWidgets.jsx).
export const PRODUCT_ANCHORS = [
  { label: 'Forensics',          href: '#forensics' },
  { label: 'Scope creep',        href: '#problem-fix' },
  { label: 'Invoice defense',    href: '#invoice' },
  { label: 'AI economics',       href: '#ai' },
];

// Sticky anchor strip with scroll-spy. An IntersectionObserver with a collapsed
// root (a horizontal line at the viewport's vertical center) reports which
// section is currently crossing that line, and we mark its anchor `is-active`.
export function ProductAnchorStrip() {
  const [active, setActive] = React.useState('');

  React.useEffect(() => {
    const targets = PRODUCT_ANCHORS
      .map(a => document.querySelector(a.href))
      .filter(Boolean);
    if (!targets.length) return;

    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) setActive('#' + entry.target.id);
      });
    }, { rootMargin: '-50% 0px -50% 0px', threshold: 0 });

    targets.forEach(t => io.observe(t));
    return () => io.disconnect();
  }, []);

  return (
    <section className="sw-trust sw-anchor-strip" aria-label={t('product.anchors.onThisPage')}>
      <div className="sw-trust-inner">
        <div className="sw-trust-label">{t('product.anchors.onThisPage')}</div>
        <div className="sw-trust-grid">
          {PRODUCT_ANCHORS.map(a => (
            <a
              key={a.href}
              href={a.href}
              className={`sw-trust-name sw-anchor-link${active === a.href ? ' is-active' : ''}`}
              aria-current={active === a.href ? 'true' : undefined}
            >
              {t('product.anchors.labels.' + a.href.slice(1))}
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

// Guarded: the build-time prerender imports this module in Node, where
// `window` is absent. Same pattern as Forms.jsx and CookieConsent.jsx.
if (typeof window !== 'undefined') Object.assign(window, {
  ProductHero, ProductAnchorStrip,
  ProductPromiseSection, ProblemFixSection, ProductModulesSection,
  HowProductWorksSection, AgencyUseCasesSection, AntiSurveillanceSection,
  ProductFaqSection,
});
